import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Checkbox, Chip, FormControlLabel, IconButton, Radio, RadioGroup, Table, TableBody, TableCell,
  TableHead, TableRow, Typography,
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { TableSection } from '../../../../../components/shared/tables/TableSection';
import { Franchise, Store, Tenant } from '../../../../../app/services/api/generated';

export type UserAccessLevel = 'store' | 'franchise' | 'tenant';

const ROWS_PER_PAGE = 10;

interface HierarchyPickerProps {
  tenants: Tenant[];
  franchises: Franchise[];
  stores: Store[];
  selectedTenantIds: number[];
  selectedFranchiseIds: number[];
  selectedStoreNumbers: string[];
  onToggleTenant: (id: number) => void;
  onToggleFranchise: (id: number) => void;
  onToggleStore: (storeNumber: string) => void;
  /** Access level is chosen upfront (radio buttons in this component's header), so the grid only lists rows of this kind. */
  level: UserAccessLevel;
  onLevelChange: (level: UserAccessLevel) => void;
  /** Owned by the parent so the same term also highlights matches in the summary panel next to this grid. */
  search: string;
  title: string;
}

const LEVEL_LABEL_KEY: Record<UserAccessLevel, string> = {
  tenant: 'tenantLevel',
  franchise: 'franchiseLevel',
  store: 'storeLevel',
};

const LevelChip = ({ level }: { level: UserAccessLevel }) => {
  const { t } = useTranslation('manageUsers');
  return <Chip label={t(LEVEL_LABEL_KEY[level])}
    size="small"
    variant="outlined"
    color="primary" />;
};

const matchesQuery = (text: string | undefined, query: string): boolean => (text ?? '').toLowerCase().includes(query);

const capitalize = (label: string): string => label.charAt(0).toUpperCase() + label.slice(1);

interface TenantRowProps {
  tenant: Tenant;
  franchises: Franchise[];
  stores: Store[];
  checked: boolean;
  onToggle: () => void;
  search: string;
}

const TenantRow = ({ tenant, franchises, stores, checked, onToggle, search }: TenantRowProps) => {
  const { t } = useTranslation('manageUsers');
  const [open, setOpen] = useState(false);
  const query = search.trim().toLowerCase();
  const tenantFranchises = useMemo(
    () => franchises.filter(franchise => franchise.tenantId === tenant.id),
    [franchises, tenant.id],
  );

  // If the tenant's own name matched, show the full breakdown; otherwise narrow it down to whichever franchise/store matched.
  const matchesSelf = !query || matchesQuery(tenant.name, query);
  const displayedFranchises = matchesSelf
    ? tenantFranchises
    : tenantFranchises.filter(franchise =>
      matchesQuery(franchise.franchiseName, query)
      || stores.some(store => store.franchiseId === franchise.franchiseId && (matchesQuery(store.storeName, query) || matchesQuery(store.storeNumber, query))));

  // Auto-expand so a search result is never hidden behind a collapsed row.
  useEffect(() => {
    if (query) setOpen(true);
  }, [query]);

  return (
    <>
      <TableRow selected={checked}>
        <TableCell width={40}>
          <Checkbox checked={checked}
            onChange={onToggle} />
        </TableCell>
        <TableCell width={110}>
          <LevelChip level="tenant" />
        </TableCell>
        <TableCell>{tenant.name}</TableCell>
        <TableCell width={40}
          align="right">
          <IconButton size="small"
            onClick={() => setOpen(!open)}
            disabled={tenantFranchises.length === 0}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
      </TableRow>
      {open && (
        <TableRow>
          <TableCell sx={{ p: 0, border: 0 }}
            colSpan={4}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ pl: 8 }}>{t('franchise')}</TableCell>
                  <TableCell>{t('stores')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedFranchises.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={2}
                      sx={{ pl: 8 }}>
                      <Typography variant="textSM"
                        color="text.secondary">{t('noResults', 'No results')}</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {displayedFranchises.map((franchise) => {
                  const franchiseStores = stores.filter(s => s.franchiseId === franchise.franchiseId);
                  const displayedStores = matchesSelf || matchesQuery(franchise.franchiseName, query)
                    ? franchiseStores
                    : franchiseStores.filter(s => matchesQuery(s.storeName, query) || matchesQuery(s.storeNumber, query));

                  return (
                    <TableRow key={franchise.franchiseId}>
                      <TableCell sx={{ pl: 8 }}>{franchise.franchiseName}</TableCell>
                      <TableCell>
                        {displayedStores.map(s => s.storeName ?? s.storeNumber).join(', ') || '\u2014'}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

interface FranchiseRowProps {
  franchise: Franchise;
  stores: Store[];
  checked: boolean;
  onToggle: () => void;
  search: string;
}

const FranchiseRow = ({ franchise, stores, checked, onToggle, search }: FranchiseRowProps) => {
  const { t } = useTranslation('manageUsers');
  const [open, setOpen] = useState(false);
  const query = search.trim().toLowerCase();
  const franchiseStores = useMemo(
    () => stores.filter(store => store.franchiseId === franchise.franchiseId),
    [stores, franchise.franchiseId],
  );

  // If the franchise's own name matched, show every store; otherwise narrow it down to the stores that matched.
  const matchesSelf = !query || matchesQuery(franchise.franchiseName, query);
  const displayedStores = matchesSelf
    ? franchiseStores
    : franchiseStores.filter(store => matchesQuery(store.storeName, query) || matchesQuery(store.storeNumber, query));

  // Auto-expand so a search result is never hidden behind a collapsed row.
  useEffect(() => {
    if (query) setOpen(true);
  }, [query]);

  return (
    <>
      <TableRow selected={checked}>
        <TableCell width={40}>
          <Checkbox checked={checked}
            onChange={onToggle} />
        </TableCell>
        <TableCell width={110}>
          <LevelChip level="franchise" />
        </TableCell>
        <TableCell>{franchise.franchiseName}</TableCell>
        <TableCell width={40}
          align="right">
          <IconButton size="small"
            onClick={() => setOpen(!open)}
            disabled={franchiseStores.length === 0}>
            {open ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
          </IconButton>
        </TableCell>
      </TableRow>
      {open && (
        <TableRow>
          <TableCell sx={{ p: 0, border: 0 }}
            colSpan={4}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={{ pl: 8 }}>{t('stores')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {displayedStores.length === 0 && (
                  <TableRow>
                    <TableCell sx={{ pl: 8 }}>
                      <Typography variant="textSM"
                        color="text.secondary">{t('noResults', 'No results')}</Typography>
                    </TableCell>
                  </TableRow>
                )}
                {displayedStores.length > 0 && (
                  <TableRow>
                    <TableCell sx={{ pl: 8 }}>
                      {displayedStores.map(s => s.storeName ?? s.storeNumber).join(', ')}
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};

type Row =
  | { kind: 'tenant'; tenant: Tenant }
  | { kind: 'franchise'; franchise: Franchise }
  | { kind: 'store'; store: Store };

const getRowName = (row: Row): string => {
  if (row.kind === 'tenant') return row.tenant.name ?? '';
  if (row.kind === 'franchise') return row.franchise.franchiseName ?? '';
  return row.store.storeName ?? row.store.storeNumber ?? '';
};

/** Single expandable grid, scoped to whichever level (tenant/franchise/store) is currently selected. */
const HierarchyPicker = ({
  tenants, franchises, stores,
  selectedTenantIds, selectedFranchiseIds, selectedStoreNumbers,
  onToggleTenant, onToggleFranchise, onToggleStore,
  level, onLevelChange, search, title,
}: HierarchyPickerProps) => {
  const { t } = useTranslation('manageUsers');
  const [page, setPage] = useState(0);

  const rows = useMemo<Row[]>(() => {
    const query = search.trim().toLowerCase();

    if (level === 'tenant') {
      return tenants
        .filter(item => {
          if (!query) return true;
          if (matchesQuery(item.name, query)) return true;

          const tenantFranchises = franchises.filter(f => f.tenantId === item.id);
          return tenantFranchises.some(f =>
            matchesQuery(f.franchiseName, query)
            || stores.some(s => s.franchiseId === f.franchiseId && (matchesQuery(s.storeName, query) || matchesQuery(s.storeNumber, query))));
        })
        .map<Row>(tenant => ({ kind: 'tenant', tenant }))
        .sort((a, b) => getRowName(a).localeCompare(getRowName(b)));
    }

    if (level === 'franchise') {
      return franchises
        .filter(item => {
          if (!query) return true;
          if (matchesQuery(item.franchiseName, query)) return true;

          return stores.some(s => s.franchiseId === item.franchiseId && (matchesQuery(s.storeName, query) || matchesQuery(s.storeNumber, query)));
        })
        .map<Row>(franchise => ({ kind: 'franchise', franchise }))
        .sort((a, b) => getRowName(a).localeCompare(getRowName(b)));
    }

    return stores
      .filter(item => matchesQuery(item.storeName, query) || matchesQuery(item.storeNumber, query))
      .map<Row>(store => ({ kind: 'store', store }))
      .sort((a, b) => getRowName(a).localeCompare(getRowName(b)));
  }, [tenants, franchises, stores, level, search]);

  // Reset to the first page whenever the scoped level or search term changes the row set.
  useEffect(() => setPage(0), [level, search]);

  const maxPages = Math.max(1, Math.ceil(rows.length / ROWS_PER_PAGE));
  const pagedRows = rows.slice(page * ROWS_PER_PAGE, page * ROWS_PER_PAGE + ROWS_PER_PAGE);

  const unitLabel = capitalize(level === 'tenant' ? t('tenants', 'Tenants') : level === 'franchise' ? t('franchises', 'Franchises') : t('stores', 'Stores'));

  const getRowId = (row: Row): string => (
    row.kind === 'tenant' ? `tenant-${row.tenant.id}` : row.kind === 'franchise' ? `franchise-${row.franchise.franchiseId}` : `store-${row.store.storeNumber}`
  );

  return (
    <TableSection
      title={title}
      columns={[
        { id: 'select', label: '' },
        { id: 'level', label: t('accessLevel') },
        { id: 'name', label: t('name', 'Name') },
        { id: 'expand', label: '' },
      ]}
      rows={pagedRows}
      getRowId={getRowId}
      recordCount={rows.length}
      recordCountLabel={unitLabel}
      actions={(
        <RadioGroup
          row
          value={level}
          onChange={(_, value) => onLevelChange(value as UserAccessLevel)}
        >
          <FormControlLabel value="tenant"
            control={<Radio size="small" />}
            label={t('tenantLevel')} />
          <FormControlLabel value="franchise"
            control={<Radio size="small" />}
            label={t('franchiseLevel')} />
          <FormControlLabel value="store"
            control={<Radio size="small" />}
            label={t('storeLevel')} />
        </RadioGroup>
      )}
      page={page}
      maxPages={maxPages}
      onPageChange={setPage}
      renderBody={() => (
        <TableBody>
          {pagedRows.map((row) => {
            if (row.kind === 'tenant') {
              return (
                <TenantRow key={`tenant-${row.tenant.id}`}
                  tenant={row.tenant}
                  franchises={franchises}
                  stores={stores}
                  checked={selectedTenantIds.includes(row.tenant.id ?? -1)}
                  onToggle={() => onToggleTenant(row.tenant.id ?? -1)}
                  search={search}
                />
              );
            }

            if (row.kind === 'franchise') {
              return (
                <FranchiseRow key={`franchise-${row.franchise.franchiseId}`}
                  franchise={row.franchise}
                  stores={stores}
                  checked={selectedFranchiseIds.includes(row.franchise.franchiseId ?? -1)}
                  onToggle={() => onToggleFranchise(row.franchise.franchiseId ?? -1)}
                  search={search}
                />
              );
            }

            return (
              <TableRow key={`store-${row.store.storeNumber}`}
                selected={selectedStoreNumbers.includes(row.store.storeNumber ?? '')}>
                <TableCell width={40}>
                  <Checkbox
                    onChange={() => onToggleStore(row.store.storeNumber ?? '')}
                    checked={selectedStoreNumbers.includes(row.store.storeNumber ?? '')} />
                </TableCell>
                <TableCell width={110}>
                  <LevelChip level="store" />
                </TableCell>
                <TableCell>{row.store.storeNumber} - {row.store.storeName}</TableCell>
                <TableCell width={40} />
              </TableRow>
            );
          })}
        </TableBody>
      )}
    />
  );
};

export default HierarchyPicker;
